import FontAwesome from '@expo/vector-icons/FontAwesome'
import {
  BottomTabBarButtonProps,
  BottomTabNavigationOptions,
} from '@react-navigation/bottom-tabs'
import {
  router,
  Tabs,
  useGlobalSearchParams,
  useNavigation,
  usePathname,
} from 'expo-router'
import { useEffect } from 'react'
import { Platform, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import TourSpot from '@/components/tour/TourSpot'
import UniActivityIndicator from '@/components/unistyles/UniActivityIndicator'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import useDeepLinkWarmup from '@/hooks/useDeepLinkWarmup'
import { useAppStore } from '@/store/useAppStore'
import trpc from '@/utils/trpc'

const TAB_BAR_HEIGHT = 68

type IconName = 'home' | 'paw'

const TabIcon = ({ color, name }: { color: string; name: IconName }) => (
  <FontAwesome color={color} name={name} size={28} />
)

const HomeIcon = ({ color }: { color: string }) => (
  <TabIcon color={color} name="home" />
)

const FostersIcon = ({ color }: { color: string }) => (
  <TabIcon color={color} name="paw" />
)

const TabButton = (
  props: BottomTabBarButtonProps & { activeBackgroundColor?: string }
) => {
  const { isWideScreen } = useBreakpoint()
  const isFosters = props['aria-label']?.toLowerCase().includes('fosters')
  const buttonStyle = [
    props.style,
    props.accessibilityState?.selected && props.style
      ? { backgroundColor: props.activeBackgroundColor }
      : undefined,
  ]

  const button = (
    <UniTouchableRipple
      accessibilityRole="button"
      borderless
      onPress={props.onPress}
      style={buttonStyle}
      testID={props.testID}
    >
      {props.children}
    </UniTouchableRipple>
  )

  if (!isFosters) return button

  // Each platform behaves a little bit differently
  const style = Platform.select({
    native: {
      flex: isWideScreen ? undefined : 1,
    },
    web: {
      flex: 1,
    },
  })

  return (
    <TourSpot id="foster-tab-button" style={style}>
      {button}
    </TourSpot>
  )
}

const RootTabLayout = () => {
  const { isWarmingUp } = useDeepLinkWarmup()

  const { theme } = useUnistyles()
  const { isLargeAndUp } = useBreakpoint()

  const navigation = useNavigation()
  const pathname = usePathname()
  const { from } = useGlobalSearchParams<{ from?: 'dashboard' | 'fosters' }>()

  // Sync the left panel tab from the `from` query param so refresh preserves which
  // context (dashboard vs fosters grid) the user came from. Manual tab switches don't
  // change pathname or from, so we won't override those. Only run when on the fosters
  // grid — not when viewing a foster detail (/fosters/[id]) — otherwise we'd navigate
  // away from the foster back to the grid. Web only for now.
  useEffect(() => {
    const isFostersGrid = pathname === '/fosters' || pathname === '/fosters/'
    if (
      Platform.OS !== 'web' ||
      !isLargeAndUp ||
      !from ||
      !isFostersGrid
    )
      return
    if (from === 'dashboard') {
      router.navigate('dashboard')
    } else if (from === 'fosters') {
      router.navigate('fosters')
    }
  }, [pathname, from, isLargeAndUp])

  const currentOrganization = useAppStore.use.currentOrganization().organization

  const tabBarBottomInset = useSafeAreaInsets().bottom

  const events = trpc.dashboard.events.useQuery(
    {
      organizationId: currentOrganization?.id || '',
    },
    {
      enabled: !!currentOrganization,
    }
  ).data

  useEffect(() => {
    navigation.setOptions({
      headerShown: false,
    })
  })

  const screenOptions: BottomTabNavigationOptions = {
    tabBarActiveTintColor: theme.colors.primary,
    animation: 'shift',
    headerShown: false,
    tabBarPosition: 'bottom',
    tabBarBadgeStyle: {
      backgroundColor: theme.colors.error,
      fontSize: 10,
    },
    tabBarItemStyle: {
      padding: 4,
    },
    tabBarStyle: {
      backgroundColor: theme.colors.surface,
      borderTopColor: theme.colors.cardBorder,
      height: tabBarBottomInset + TAB_BAR_HEIGHT,
      paddingTop: 8,
    },
    tabBarButton: (props: BottomTabBarButtonProps) => (
      <TabButton
        {...props}
        activeBackgroundColor={screenOptions.tabBarActiveBackgroundColor}
      />
    ),
  }

  if (isLargeAndUp) {
    screenOptions.tabBarPosition = 'left'
    screenOptions.tabBarVariant = 'material'
    screenOptions.tabBarLabelPosition = 'below-icon'
    screenOptions.tabBarStyle = {
      backgroundColor: theme.colors.surface,
      minWidth: 120,
      maxWidth: 200,
      borderRightColor: theme.colors.cardBorder,
    }

    screenOptions.tabBarLabelStyle = {
      fontSize: 13,
      marginLeft: 10,
    }

    screenOptions.tabBarIconStyle = {
      width: 28,
      height: 28,
    }

    screenOptions.tabBarActiveBackgroundColor = theme.colors.surfaceVariant
    screenOptions.tabBarActiveTintColor = theme.colors.primary
    screenOptions.tabBarInactiveTintColor = theme.colors.onSurfaceVariant
  }

  return (
    <>
      {isWarmingUp && (
        <View style={styles.warmupOverlay}>
          <UniActivityIndicator size="large" />
        </View>
      )}
      <Tabs
        backBehavior="none"
        detachInactiveScreens={false}
        initialRouteName="dashboard"
        screenOptions={screenOptions}
      >
        <Tabs.Screen
          name="dashboard"
          options={{
            title: 'Dashboard',
            tabBarIcon: HomeIcon,
            tabBarBadge: events?.[0]?.overdueCount || undefined,
            tabBarButtonTestID: 'dashboard-tab',
            tabBarAccessibilityLabel: 'Dashboard Tab',
          }}
        />
        <Tabs.Screen
          name="fosters"
          options={{
            lazy: false,
            title: 'Fosters',
            tabBarIcon: FostersIcon,
            tabBarButtonTestID: 'fosters-tab',
            tabBarAccessibilityLabel: 'Fosters Tab',
          }}
        />

        <Tabs.Screen name="index" redirect />
      </Tabs>
    </>
  )
}

export default RootTabLayout

const styles = StyleSheet.create((theme) => ({
  warmupOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    justifyContent: 'center',
    zIndex: 9999,
  },
}))
